import React, { useState, useEffect } from "react";
import { Sparkles, ChevronRight, Menu, X, Sun, Moon } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

export default function Header({ darkMode, toggleDarkMode }) {
  const location = useLocation();
  const isPrivacyPage = location.pathname === "/privacy";

  const navItems = [
    { label: "Home", href: "#home" },
    { label: "About", href: "#about" },
    { label: "How to Use", href: "#how" },
    { label: "Privacy Policy", href: "/privacy" },
  ];

  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

useEffect(() => {
  if (!isPrivacyPage) {
    const sections = navItems
      .filter((item) => item.href.startsWith("#")) // only hash links
      .map((item) => document.querySelector(item.href));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-50% 0px -50% 0px" }
    );

    sections.forEach((section) => section && observer.observe(section));

    return () => sections.forEach((section) => section && observer.unobserve(section));
  }
}, [isPrivacyPage]);


  const handleLinkClick = (e, href) => {
    e.preventDefault();
    if (href.startsWith("/")) return; // for react-router links
    const target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: "smooth" });
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur bg-white/70 dark:bg-gray-900/70 border-b border-slate-200/50 dark:border-gray-700/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between h-16 items-center">
        <Link to="/" className="flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
          <Sparkles className="w-6 h-6 text-indigo-500" /> QuickCaptionIO
        </Link>

        {!isPrivacyPage ? (
          <nav className="hidden md:flex gap-6 items-center">
            {navItems.map((item) =>
              item.href.startsWith("/") ? (
                <Link
                  key={item.href}
                  to={item.href}
                  className="text-slate-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition"
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => handleLinkClick(e, item.href)}
                  className={`transition ${
                    activeSection === item.href.slice(1)
                      ? "text-indigo-600 dark:text-indigo-400 font-semibold"
                      : "text-slate-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400"
                  }`}
                >
                  {item.label}
                </a>
              )
            )}
           <a
  href="https://apps.apple.com/jp/app/quick-captionai/id6752661054?l=en-USQuick-CaptionAI"
  target="_blank"
  rel="noopener noreferrer"
  className="bg-indigo-500 text-white px-4 py-2 rounded-xl hover:bg-indigo-600 transition flex items-center gap-1"
>
  Download App <ChevronRight className="w-4 h-4" />
</a>

            <button
              onClick={toggleDarkMode}
              className="ml-4 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-gray-700 transition"
            >
              {darkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-gray-800" />}
            </button>
          </nav>
        ) : (
          <Link
            to="/"
            className="bg-indigo-500 text-white px-4 py-2 rounded-xl hover:bg-indigo-600 transition flex items-center gap-1"
          >
            Home
          </Link>
        )}

        {!isPrivacyPage && (
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-gray-700 transition"
            >
              {darkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-gray-800" />}
            </button>
            <button
              onClick={() => setMobileOpen(true)}
              className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-gray-700 transition"
            >
              <Menu className="w-6 h-6 text-slate-900 dark:text-white" />
            </button>
          </div>
        )}
      </div>

{/* Mobile menu */}
{!isPrivacyPage && (
  <>
    {/* Background overlay */}
    <div
      className={`fixed inset-0 z-40 backdrop-blur-sm bg-black/30 transition-opacity duration-300 ${
        mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
      onClick={() => setMobileOpen(false)}
    ></div>

    {/* Menu panel */}
    <aside
      className={`fixed right-0 top-0 z-50 h-full w-72 bg-white dark:bg-gray-900 shadow-2xl p-6 flex flex-col transform transition-transform duration-300 ${
        mobileOpen ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <div className="flex items-center justify-between mb-6">
        <span className="font-semibold text-slate-900 dark:text-white">Menu</span>
        <button
          onClick={() => setMobileOpen(false)}
          className="rounded-xl p-2 hover:bg-slate-100 dark:hover:bg-gray-700 transition"
        >
          <X className="w-6 h-6 text-slate-900 dark:text-white" />
        </button>
      </div>

      <div className="flex flex-col gap-3">
        {navItems.map((item) => (
          <a
            key={item.href}
            href={item.href}
            onClick={(e) => handleLinkClick(e, item.href)}
            className={`rounded-xl px-3 py-2 transition ${
              activeSection === item.href.slice(1)
                ? "text-indigo-600 dark:text-indigo-400 font-semibold"
                : "text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-gray-700"
            }`}
          >
            {item.label}
          </a>
        ))}
      </div>

     <a
  href="https://apps.apple.com/jp/app/quick-captionai/id6752661054?l=en-US"
  target="_blank"
  rel="noopener noreferrer"
  className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 dark:border-gray-700 px-4 py-3 text-sm font-semibold shadow-sm hover:shadow transition-all bg-white dark:bg-gray-900 text-slate-900 dark:text-white"
>
  Download App <ChevronRight className="w-4 h-4" />
</a>

    </aside>
  </>
)}


    </header>
  );
}
